import { useCallback } from 'react';
import { updateExperienceFields } from '@/data/experiencesRepo';
import type { HistoryInfoType } from '@/types/types_history';
import { useCurrentUserSimple } from './useCurrentUserSimple';
import { applyHistoryToUpdate, type HistorizedSaveType } from './util_history';
import type { SaveStack } from './util_saveStack';

/**
 * Returns an `autosave(userAction, fields, currentHistory)` function that writes
 * changed fields of one experience to Firestore, registering the operation on
 * the save stack so the UI can show save status. `userAction` is a short human
 * label (e.g. "Edit name") shown in the pending-saves tooltip and the history
 * panel.
 *
 * The history step is appended here rather than by the caller, so no write path
 * can leave a change unrecorded. The new history comes back synchronously; the
 * `saved` promise resolves when Firestore confirms.
 */
export function useAutosaveExperience(experienceId: string | undefined, saveStack: SaveStack) {
  const userSimple = useCurrentUserSimple();

  return useCallback(
    (
      userAction: string,
      fields: Record<string, unknown>,
      currentHistory: HistoryInfoType | undefined,
      isUndoStep = false,
    ): HistorizedSaveType => {
      if (!experienceId) {
        throw new Error(`Cannot autosave: no experience id. action=[${userAction}]`);
      }
      const { updateObj, newHistory } = applyHistoryToUpdate(
        fields,
        userAction,
        currentHistory,
        userSimple,
        isUndoStep,
      );

      const opId = crypto.randomUUID();
      saveStack.pushToSaveStack({ id: opId, userAction });
      // Not `.finally`: if the write throws, we deliberately leave the item on
      // the stack rather than flash a misleading "saved" state.
      const saved = updateExperienceFields(experienceId, updateObj).then(() => {
        saveStack.removeFromSaveStack(opId);
      });

      return { newHistory, saved };
    },
    [experienceId, saveStack, userSimple],
  );
}
